import { useState, useEffect } from "react";
import { Button } from "antd";
import { PlayCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { refreshFacebookMessageAttachments } from "@api/services/facebook";
import { useAppDispatch } from "@stores/redux/store";
import { updateMessageUrl } from "@features/messages/messagesSlice";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";

interface MessageVideoProps {
  src: string;
  messageId?: string;
  conversationId?: string;
  pageId?: string | null;
}

export const MessageVideo = ({
  src,
  messageId,
  conversationId,
  pageId,
}: MessageVideoProps) => {
  const { success, error: showError } = useNotificationContext();
  const [videoError, setVideoError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const dispatch = useAppDispatch();

  const handleRetry = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!messageId || !pageId) {
      showError("Không tìm thấy thông tin message hoặc page");
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await refreshFacebookMessageAttachments({
        messageId,
        pageId,
      });

      if (response.code === 0) {
        const newUrl = response.data?.newUrl;
        if (newUrl && messageId && conversationId) {
          dispatch(
            updateMessageUrl({
              conversationId,
              messageId,
              newUrl,
            })
          );
          setCurrentSrc(newUrl);
          setVideoError(false);
          setRetryKey((prev) => prev + 1);
          success("Đã tải lại video thành công");
        } else {
          success("Đang tải lại video...");
          setVideoError(false);
          setRetryKey((prev) => prev + 1);
        }
      } else {
        showError(response.message || "Tải lại video thất bại");
      }
    } catch (error) {
      console.error("Error refreshing video attachments:", error);
      showError("Tải lại video thất bại");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (src) {
      setCurrentSrc(src);
    }
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    console.error("❌ MessageItem: Video error", {
      videoUrl: currentSrc,
      error: e,
      target: e.target,
      messageId,
    });
    setVideoError(true);
  };

  if (!currentSrc) {
    return (
      <div className={styles.videoPlaceholder}>
        <div className={styles.placeholderIcon}>
          <PlayCircleOutlined style={{ fontSize: 24 }} />
        </div>
        <span className={styles.placeholderText}>Không có file video</span>
        {messageId && pageId && (
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            className={styles.retryButton}
            size="small"
            onClick={handleRetry}
            loading={isRefreshing}
            disabled={isRefreshing}
          >
            Tải lại
          </Button>
        )}
      </div>
    );
  }

  if (videoError) {
    return (
      <div className={styles.videoPlaceholder}>
        <div className={styles.placeholderIcon}>
          <PlayCircleOutlined style={{ fontSize: 24 }} />
        </div>
        <span className={styles.placeholderText}>Video đã hết hạn</span>
        {messageId && pageId && (
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            className={styles.retryButton}
            size="small"
            onClick={handleRetry}
            loading={isRefreshing}
            disabled={isRefreshing}
          >
            Tải lại
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.videoWrapper}>
      <video
        key={`${currentSrc}-${retryKey}`}
        src={currentSrc}
        controls
        className={styles.video}
        preload="metadata"
        playsInline
        onError={handleError}
      />
    </div>
  );
};
